import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getHomeFeed } from '@/services/homeService';

import { getActivitiesFeed } from '@/services/activityService';
import { getCoinPostsFeed, getCoinPostsTotal } from '@/services/coinService';
import { getBrowserLocation } from '@/lib/geo';

import { ActivityTypeNum, HelpTypeNum } from '@/lib/types';

import type { CoinPost, CoinCategory, Activity, ApiActivity, ApiCoinPost } from '@/lib/types';
import type { TypeFilter, TimeFilter } from '@/pages/home/components/FilterBar';

// ============================================================
// MAPPING API -> FRONT
// ============================================================

function activityTypeNumToFront(type: ActivityTypeNum, helpType?: HelpTypeNum): Activity['type'] {
  if (type === ActivityTypeNum.Social) return 'SOCIAL';

  if (type === ActivityTypeNum.Sport) return 'SPORT';

  if (helpType === HelpTypeNum.Offer) return 'HELP_OFFER';

  return 'HELP_REQUEST';
}

function timeCategory(startsAt?: string | null): 'TODAY' | 'WEEK' | 'UPCOMING' {
  if (!startsAt)
    return 'UPCOMING';

  const start = new Date(startsAt);
  const now = new Date();

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);

  if (start >= todayStart && start < tomorrowStart)
    return 'TODAY';

  const daysSinceMonday = (now.getDay() + 6) % 7;

  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - daysSinceMonday);

  const nextWeekStart = new Date(weekStart);
  nextWeekStart.setDate(nextWeekStart.getDate() + 7);

  if (start >= weekStart && start < nextWeekStart)
    return 'WEEK';

  return 'UPCOMING';
}


function formatTime(startsAt?: string | null): string {
  if (!startsAt)
    return 'À venir';

  const d = new Date(startsAt);
  const today = new Date();

  const isToday =
    d.toDateString() === today.toDateString();

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const isTomorrow =
    d.toDateString() === tomorrow.toDateString();

  const time = d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (isToday)
    return `Aujourd'hui · ${time}`;

  if (isTomorrow)
    return `Demain · ${time}`;

  const inSevenDays = new Date(today);
  inSevenDays.setDate(today.getDate() + 7);

  if (d <= inSevenDays) {
    const day = d.toLocaleDateString('fr-FR', {
      weekday: 'long',
    });

    const dayNumber = d.toLocaleDateString('fr-FR', {
      day: 'numeric',
    });

    const month = d.toLocaleDateString('fr-FR', {
      month: 'short',
    }).replace('.', '');

    return `${day} ${dayNumber} ${month} · ${time}`;
  }

  const date = d.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return `${date} · ${time}`;
}


function formatActivityTime(startsAt?: string | null, endsAt?: string | null): string {
  if (!startsAt) return 'À venir';

  const start = new Date(startsAt);
  const end = endsAt ? new Date(endsAt) : null;

  const now = new Date();
  const sevenDaysLater = new Date(
    now.getTime() + 7 * 24 * 60 * 60 * 1000
  );

  const isSameDay = (a: Date, b: Date) =>
    a.toDateString() === b.toDateString();

  const isWithinSevenDays = (date: Date) =>
    date.getTime() <= sevenDaysLater.getTime();

  const formatHour = (date: Date) =>
    date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatShortDay = (date: Date) => {
    const day = date.toLocaleDateString('fr-FR', {
      weekday: 'long',
    });

    const dayNumber = date.toLocaleDateString('fr-FR', {
      day: 'numeric',
    });

    const month = date.toLocaleDateString('fr-FR', {
      month: 'short',
    }).replace('.', '');

    return `${day} ${dayNumber} ${month}`;
  };

  const formatFullDate = (date: Date) =>
    date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });

  if (!end) {
    return formatTime(startsAt);
  }

  if (isWithinSevenDays(start)) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const startLabel = isSameDay(start, today)
      ? "Aujourd'hui"
      : isSameDay(start, tomorrow)
        ? "Demain"
        : formatShortDay(start);

    if (isSameDay(start, end)) {
      return `${startLabel} · ${formatHour(start)} - ${formatHour(end)}`;
    }

    const endLabel = isSameDay(end, today)
      ? "Aujourd'hui"
      : isSameDay(end, tomorrow)
        ? "Demain"
        : formatShortDay(end);

    return `${startLabel} · ${formatHour(start)} - ${endLabel} · ${formatHour(end)}`;
  }


  if (isSameDay(start, end)) {
    return `${formatFullDate(start)} · ${formatHour(start)} - ${formatHour(end)}`;
  }

  return `${formatFullDate(start)} · ${formatHour(start)} - ${formatFullDate(end)} · ${formatHour(end)}`;
}




export function apiActivityToFront(a: ApiActivity): Activity {
  const author = (a as any).author ?? (a as any).creator ?? null;

  const authorName = author?.displayName ?? author?.name ?? author?.email ?? 'Anonyme';

  const count = (a as any).participantCount ?? (a as any).participantsCount ?? (a as any).participantcount ?? 0;


  return {
    id: a.id,
    type: activityTypeNumToFront(a.type, a.helpType),
    title: a.title,
    description: a.description ?? '',
    createdAt: a.createdAt,
    organizer: authorName,
    time: formatActivityTime(
      a.nextSessionStartsAt ?? a.startsAt,
      a.endsAt
    ),
    endsAt: a.endsAt ?? null,
    nextSessionStartsAt: a.nextSessionStartsAt ?? null,
    upcomingSessionsCount: a.upcomingSessionsCount ?? 0,
    timeCategory: timeCategory(
      a.nextSessionStartsAt ?? a.startsAt
    ),
    distanceKm: (a as any).distanceKm ?? (a as any).distancekm ?? 0,
    location: a.publicLocationLabel ?? (a as any).publiclocationlabel ?? 'Dole',
    latitude: a.latitude ?? null,
    longitude: a.longitude ?? null,
    participants: count,
    commentsCount: a.commentsCount ?? 0,
    maxParticipants: a.maxParticipants ?? null,
    tags: a.tags ?? [],
    isOwner: a.isOwner ?? false,
  }
}

// ============================================================
// COIN MAPPING
// ============================================================

const COIN_CAT_MAP: Record<number, CoinCategory> = {
  1: 'VU',
  2: 'CONFESSION',
  3: 'COUP_DE_COEUR',
  4: 'QUESTION',
};

function formatTimeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);

  if (mins < 60)
    return `il y a ${mins} min`;

  const hours = Math.floor(mins / 60);

  if (hours < 24)
    return `il y a ${hours} h`;

  const days = Math.floor(hours / 24);

  if (days === 1)
    return 'hier';

  return `il y a ${days} jours`;
}

export function apiCoinPostToFront(p: ApiCoinPost): CoinPost {
  return {
    id: p.id,
    category: COIN_CAT_MAP[p.category] ?? 'VU',
    content: p.content,
    authorAlias: p.authorAlias ?? 'Anonyme',
    city: 'DOLE',
    time: formatTimeAgo(p.createdAt),
    createdAt: p.createdAt,
    likes: p.reactionsCount ?? 0,
    comments: [],
    hasReacted: p.hasReacted ?? false,
    commentCount:
      p.commentsCount ?? 0,
  };
}
// ============================================================
// FEED
// ============================================================
export const FEED_PAGE_SIZE = 10;

export interface FeedState {
  activities: Activity[];
  coinPosts: CoinPost[];
  loading: boolean;
  error: string | null;
}

export function useFeed(selectedType: TypeFilter, selectedDistance: number, selectedTime: TimeFilter, userId?: string, currentPage: number = 1) {


  const queryClient = useQueryClient();

  const [loadedActivities, setLoadedActivities] = useState<Activity[]>([]);
  const [loadedCoinPosts, setLoadedCoinPosts] = useState<CoinPost[]>([]);

  // ----------------------------------------------------------
  // Paramètres Activity
  // ----------------------------------------------------------

  const actTypeParam =
    selectedType === 'SOCIAL'
      ? ActivityTypeNum.Social
      : selectedType === 'SPORT'
        ? ActivityTypeNum.Sport
        : selectedType === 'HELP'
          ? ActivityTypeNum.Help
          : undefined;

  // ----------------------------------------------------------
  // Query principale
  // ----------------------------------------------------------

  const query = useQuery({
    queryKey: ['home-feed', userId ?? 'anonymous', selectedType, selectedDistance, selectedTime, currentPage,],

    queryFn: async () => {
      const { latitude, longitude } = await getBrowserLocation();


      if (selectedType === 'ALL') {
        const homeFeed = await getHomeFeed({
          Latitude: latitude,
          Longitude: longitude,
          RadiusKm: selectedDistance,
          Time: selectedTime,
          Page: currentPage,
          PageSize: FEED_PAGE_SIZE,
        });

        return {
          homeFeed,

          apiActivities: {
            items: homeFeed.items
              .filter((item) => item.kind === 'activity')
              .map((item) => item.activity),

            page: homeFeed.page,
            pageSize: homeFeed.pageSize,
            totalItems: homeFeed.totalItems,
            totalPages: homeFeed.totalPages,
          },

          apiCoinPosts: homeFeed.items
            .filter((item) => item.kind === 'coin')
            .map((item) => item.coinPost),
        };
      }

      const activityPromise =
        selectedType !== 'COIN'
          ? getActivitiesFeed({
            Latitude: latitude,
            Longitude: longitude,
            RadiusKm: selectedDistance,
            Time: selectedTime,
            ...(actTypeParam !== undefined ? { Type: actTypeParam } : {}),
            Page: currentPage,
            PageSize: FEED_PAGE_SIZE,
          })
          : Promise.resolve({
            items: [],
            page: currentPage,
            pageSize: FEED_PAGE_SIZE,
            totalItems: 0,
            totalPages: 1,
          });

      const coinPromise = selectedType === 'COIN'
        ? getCoinPostsFeed({
          RadiusKm: selectedDistance,
          PageSize: FEED_PAGE_SIZE,
        })
        : Promise.resolve([]);

      const coinTotalPromise =
        selectedType === 'COIN'
          ? getCoinPostsTotal({
            RadiusKm: selectedDistance,
            PageSize: FEED_PAGE_SIZE,
          })
          : Promise.resolve(0);

      const [apiActivities, apiCoinPosts, coinTotalItems] = await Promise.all([activityPromise, coinPromise, coinTotalPromise]);

      return {
        apiActivities,
        apiCoinPosts,
        coinTotalItems
      };
    },
  });

  // ----------------------------------------------------------
  // Conversion API -> front
  // ----------------------------------------------------------

  const activities = useMemo(() => {
    return (query.data?.apiActivities.items ?? []).map(apiActivityToFront);
  }, [query.data?.apiActivities.items]);


  const coinPosts =
    useMemo(() => {
      return (query.data?.apiCoinPosts ?? []).map(apiCoinPostToFront);
    }, [query.data?.apiCoinPosts,]);


  useEffect(() => {
    if (!query.data) return;

    if (currentPage === 1) {
      setLoadedActivities(activities);
      setLoadedCoinPosts(coinPosts);
      return;
    }

    setLoadedActivities((prev) => [
      ...prev,
      ...activities.filter(
        (activity) => !prev.some((item) => item.id === activity.id)
      ),
    ]);

    setLoadedCoinPosts((prev) => [
      ...prev,
      ...coinPosts.filter(
        (post) => !prev.some((item) => item.id === post.id)
      ),
    ]);
  }, [activities, coinPosts, currentPage, query.data]);

  // ----------------------------------------------------------
  // Compatibilité avec Home.tsx actuel
  // ----------------------------------------------------------

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['home-feed'], });
  };

  const error = query.error instanceof Error
    ? query.error.message
    : query.error
      ? 'Erreur de chargement'
      : null;

  const displayedCount = loadedActivities.length + loadedCoinPosts.length;

  const totalItems = query.data?.homeFeed?.totalItems ?? (selectedType === 'COIN' ? query.data?.coinTotalItems ?? 0 : query.data?.apiActivities.totalItems ?? 0);

  const hasMore = displayedCount < totalItems;
  const loadingMore = currentPage > 1 && query.isFetching;

  return {
    activities: loadedActivities,
    coinPosts: loadedCoinPosts,
    loading: currentPage === 1 && query.isLoading,
    loadingMore,
    error,
    refresh,

    totalItems:
      query.data?.homeFeed?.totalItems ??
      (selectedType === 'COIN'
        ? query.data?.coinTotalItems ?? 0
        : query.data?.apiActivities.totalItems ?? 0),

    totalPages:
      query.data?.homeFeed?.totalPages ??
      query.data?.apiActivities.totalPages ??
      1,

    displayedCount,
    hasMore,
  };
}